import { ArrowUpRight, ArrowDownRight, ArrowLeftRight, RefreshCw } from 'lucide-react';
import type { WiseStatementTransaction } from '@/lib/wiseService';

function formatCurrencyValue(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function getTransactionIcon(type: string) {
  switch (type) {
    case 'CREDIT':
      return { icon: ArrowDownRight, bg: 'bg-emerald-500/10', color: 'text-emerald-800' };
    case 'DEBIT':
      return { icon: ArrowUpRight, bg: 'bg-red-500/10', color: 'text-red-600' };
    case 'CONVERSION':
      return { icon: ArrowLeftRight, bg: 'bg-sky-500/10', color: 'text-sky-600' };
    default:
      return { icon: RefreshCw, bg: 'bg-white/10', color: 'text-white' };
  }
}

function formatTransactionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface StatementSectionProps {
  transactions: WiseStatementTransaction[];
  loading: boolean;
  currency: string;
}

export default function StatementSection({ transactions, loading }: StatementSectionProps) {
  if (loading) {
    return (
      <div className="glass-panel rounded-2xl p-8">
        <div className="flex items-center justify-center gap-3">
          <div className="w-5 h-5 border-2 border-white/10 border-t-teal-600 rounded-full animate-spin" />
          <span className="text-sm text-white">Loading transactions...</span>
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="glass-panel rounded-2xl p-8">
        <div className="flex flex-col items-center justify-center">
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-3">
            <ArrowLeftRight size={24} className="text-white" />
          </div>
          <h3 className="text-sm font-semibold text-white mb-1">No transactions</h3>
          <p className="text-xs text-white">No activity found for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-teal-500/10/80 flex items-center justify-center">
          <ArrowLeftRight size={16} className="text-teal-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Recent Transactions</h2>
        <span className="text-[11px] text-white ml-auto">{transactions.length} total</span>
      </div>
      <div className="space-y-2 max-h-[480px] overflow-y-auto">
        {transactions.map((txn, idx) => {
          const { icon: Icon, bg, color } = getTransactionIcon(txn.type);
          const isCredit = txn.type === 'CREDIT';
          const amount = txn.amount.value;

          return (
            <div
              key={`${txn.referenceNumber}-${idx}`}
              className="flex items-center justify-between p-3.5 glass-inner rounded-xl glass-hover"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
                  <Icon size={15} className={color} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-white truncate">
                    {txn.details.description || txn.details.senderName || txn.details.recipientName || txn.details.type}
                  </p>
                  <p className="text-[10px] text-white">
                    {formatTransactionDate(txn.date)}
                    {txn.details.paymentReference && ` - ${txn.details.paymentReference}`}
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <p className={`text-xs font-bold tabular-nums ${isCredit ? 'text-emerald-700' : 'text-red-600'}`}>
                  {isCredit ? '+' : '-'}{formatCurrencyValue(Math.abs(amount), txn.amount.currency)}
                </p>
                {txn.totalFees.value > 0 && (
                  <p className="text-[10px] text-white">
                    Fee: {formatCurrencyValue(txn.totalFees.value, txn.totalFees.currency)}
                  </p>
                )}
                {txn.exchangeDetails?.rate && (
                  <p className="text-[10px] text-sky-600">
                    Rate: {txn.exchangeDetails.rate.toFixed(4)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
